import { useCallback, useState } from 'react'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import {
  useLabelsStore,
  useGetLabelUsage,
  useRemoveLabel,
  type Label,
} from '@zendia/frontend-shared'
import { Labels } from '../../../components/Labels/Labels'
import { ConfirmDialog } from '../../../components/Modal'
import { axiosAuthenticatedRequest } from '../../../utilities/axiosClient'
import { AddTwo, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { ActionBar } from '../../../components/ActionBar'

export const Route = createFileRoute('/_authenticated/settings/labels')({
  component: LabelsSettings,
})

function LabelsSettings() {
  const navigate = useNavigate()
  const { labels } = useLabelsStore()
  const [labelPendingDelete, setLabelPendingDelete] = useState<Label | null>(null)

  const { data: usageResponse } = useGetLabelUsage(axiosAuthenticatedRequest)
  const usage = usageResponse?.data?.usage

  const { mutateAsync: removeLabel, isPending: isDeleting } =
    useRemoveLabel(axiosAuthenticatedRequest)

  const onCreateLabel = useCallback(() => {
    navigate({ to: '/settings/labels/$labelId/modal', params: { labelId: 'new' } })
  }, [navigate])

  const onEditLabel = useCallback(
    (label: Label) => {
      navigate({ to: '/settings/labels/$labelId/modal', params: { labelId: label.id } })
    },
    [navigate],
  )

  const onDeleteLabel = useCallback((label: Label) => {
    setLabelPendingDelete(label)
  }, [])

  const onConfirmDelete = useCallback(async () => {
    if (!labelPendingDelete) {
      return
    }

    try {
      await removeLabel(labelPendingDelete.id)
    } catch (error) {
      console.error('Error deleting label:', error)
    } finally {
      setLabelPendingDelete(null)
    }
  }, [labelPendingDelete, removeLabel])

  // Informational only - cleanup always runs against every labelled collection regardless
  // of what this says, since the count was read in an earlier request.
  const pendingTotal = labelPendingDelete ? (usage?.[labelPendingDelete.id]?.total ?? 0) : 0

  const backButton = (
    <button className="inline-block cursor-pointer" onClick={() => navigate({ to: '/settings' })}>
      <ChevronBackward className="size-6" />
    </button>
  )

  const createButton = (
    <button
      className="bg-purple-600 hover:bg-purple-700 text-gray-200 font-medium p-2 md:py-2 md:px-3 transition-colors cursor-pointer rounded-xl w-fit self-end"
      onClick={onCreateLabel}
    >
      <AddTwo className="size-6 md:size-5 inline-block" />
      <span className="hidden md:inline-block ms-2">Create New Label</span>
    </button>
  )

  return (
    <>
      <ActionBar title="Labels" startAction={backButton} />
      <div className="flex flex-col p-4 gap-4">
        {createButton}
        <div>
          <Labels
            labels={labels || []}
            usage={usage}
            onEdit={onEditLabel}
            onDelete={onDeleteLabel}
          />
        </div>
        <Outlet />
      </div>
      <ConfirmDialog
        open={labelPendingDelete !== null}
        negative
        title="Delete label"
        message={
          pendingTotal > 0 ? (
            <>
              &ldquo;{labelPendingDelete?.value}&rdquo; is used by {pendingTotal} item
              {pendingTotal === 1 ? '' : 's'}. Deleting it removes the label from{' '}
              {pendingTotal === 1 ? 'that item' : 'those items'}. This cannot be undone.
            </>
          ) : (
            <>
              &ldquo;{labelPendingDelete?.value}&rdquo; isn&rsquo;t used by anything. This cannot be
              undone.
            </>
          )
        }
        confirmLabel="Delete"
        isPending={isDeleting}
        onConfirm={onConfirmDelete}
        onCancel={() => setLabelPendingDelete(null)}
      />
    </>
  )
}
